/**
 * The single API module. Wraps the openapi-fetch client generated from FastAPI's OpenAPI schema
 * (`npm run api:types` → `schema.d.ts`; never edit that file by hand).
 *
 * - Same-origin cookie session (`sv_session`, httpOnly); every path already starts with `/api`.
 * - Mutating requests carry `X-CSRF-Token` (token from login or `/api/auth/me`).
 * - Errors are RFC 7807 problem+json and are normalised to `ApiError`.
 * - A 401 on any call except login notifies the auth context, which sends the user to login.
 */
import createClient, { type Middleware } from "openapi-fetch";
import { t } from "../i18n/t";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type Me = Schemas["MeOut"];
export type OrgRef = Schemas["OrgRef"];
export type KommuneOverview = Schemas["KommuneOverview"];
export type SchoolSummary = Schemas["SchoolSummary"];
export type RunInfo = Schemas["RunInfo"];
export type FindingItem = Schemas["FindingItem"];
export type FindingDetail = Schemas["FindingDetail"];
export type FindingPage = Schemas["FindingPage"];
export type FindingStatus = Schemas["StatusIn"]["status"];
export type StatusOut = Schemas["StatusOut"];
export type SchoolMap = Schemas["SchoolMap"];
export type ChangeEventOut = Schemas["ChangeEventOut"];
export type ChangeEventDetail = Schemas["ChangeEventDetail"];
export type MemberOut = Schemas["MemberOut"];
export type InvitationOut = Schemas["InvitationOut"];
export type InvitationInfo = Schemas["InvitationInfo"];
export type Role = Schemas["RoleIn"]["role"];
export type AdminOrgOut = Schemas["AdminOrgOut"];
export type AdminKommuneOut = Schemas["AdminKommuneOut"];
export type AdminRunOut = Schemas["AdminRunOut"];
export type JobOut = Schemas["JobOut"];
export type ExportOut = Schemas["ExportOut"];
export type ExportKind = Schemas["ExportIn"]["kind"];
export type ExportStatus = ExportOut["status"];
export type OrgKind = NonNullable<Schemas["OrgIn"]["kind"]>;
export type FindingsQuery = NonNullable<
  paths["/api/orgs/{org_id}/kommuner/{nummer}/findings"]["get"]["parameters"]["query"]
>;
export type JobStatusFilter = NonNullable<
  NonNullable<paths["/api/admin/jobs"]["get"]["parameters"]["query"]>["status"]
>;

export const FINDING_STATUSES: readonly FindingStatus[] = [
  "ny",
  "planlagt",
  "utfort",
  "ikke_relevant",
];
export const SEVERITIES = ["high", "medium", "low"] as const;
export type Severity = (typeof SEVERITIES)[number];
export const ROLES: readonly Role[] = ["member", "owner"];

/** A problem+json error from the API (or a network failure, status 0). */
export interface FieldError {
  loc: string[];
  msg: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly detail: string | undefined;
  readonly errors: FieldError[];

  constructor(status: number, title: string, detail?: string, errors: FieldError[] = []) {
    super(detail ?? title);
    this.name = "ApiError";
    this.status = status;
    this.title = title;
    this.detail = detail;
    this.errors = errors;
  }

  /** Norwegian text suitable for showing to the user. */
  get userMessage(): string {
    if (this.status === 0) return t("errors.network");
    return this.detail ?? this.title;
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function parseFieldErrors(v: unknown): FieldError[] {
  if (!Array.isArray(v)) return [];
  return v.filter(isRecord).map((e) => ({
    loc: Array.isArray(e.loc) ? e.loc.map(String) : [],
    msg: typeof e.msg === "string" ? e.msg : "",
  }));
}

/** Build an ApiError from an openapi-fetch error body and its response. */
export function problemFrom(body: unknown, response: Response): ApiError {
  if (isRecord(body)) {
    const title = typeof body.title === "string" ? body.title : t("errors.generic");
    // FastAPI's default 422 shape has `detail` as a list; our handler sends problem+json.
    const detail = typeof body.detail === "string" ? body.detail : undefined;
    const errors = parseFieldErrors(body.errors ?? (Array.isArray(body.detail) ? body.detail : []));
    return new ApiError(response.status, title, detail, errors);
  }
  return new ApiError(response.status, t("errors.generic"));
}

/** Normalise anything thrown by a request into an ApiError. */
export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  return new ApiError(0, t("errors.network"));
}

let csrfToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Called on a 401 from any endpoint except login (an expired or missing session). */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
const NO_SESSION_REDIRECT = ["/api/auth/login", "/api/auth/me"];

const sessionMiddleware: Middleware = {
  onRequest({ request }) {
    if (!SAFE_METHODS.has(request.method) && csrfToken) {
      request.headers.set("X-CSRF-Token", csrfToken);
    }
    return request;
  },
  onResponse({ request, response }) {
    if (response.status === 401) {
      const path = new URL(request.url).pathname;
      if (!NO_SESSION_REDIRECT.includes(path)) unauthorizedHandler?.();
    }
    return undefined;
  },
};

export const api = createClient<paths>({
  // Paths in the schema already include the `/api` prefix, so the base is the page's own origin
  // (an absolute URL keeps `Request` happy outside browsers too, e.g. in tests).
  baseUrl: typeof window === "undefined" ? "" : window.location.origin,
  credentials: "same-origin",
  // Resolve fetch lazily so tests can stub `globalThis.fetch` after this module is loaded.
  fetch: (request: Request) => globalThis.fetch(request),
});
api.use(sessionMiddleware);

type ApiResult<T> = { data?: T; error?: unknown; response: Response };

/** Await an openapi-fetch call and return its data, throwing ApiError on any failure. */
export async function unwrap<T>(call: Promise<ApiResult<T>>): Promise<T> {
  let result: ApiResult<T>;
  try {
    result = await call;
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw toApiError(err);
  }
  if (!result.response.ok) throw problemFrom(result.error, result.response);
  return result.data as T;
}
